import React, { useEffect, useRef, useState } from "react";
import { useWeb3 } from "../../context/Web3Context";
import { useNotification } from "../../hooks/useNotification";
import { bytes32ToString, shortAddress } from "../../utils/format";
import { DAILY_CLUB_NAMES } from "../../config/constants";
import Button from "../../components/Common/Button";
import { Card } from "../../components/Common/Card";

const PAGE_SIZE = 15;

// 7 royalty ranks (weekly/monthly clubs removed in new contract)
const RANK_TABS = [
  { key: "d1", label: "Starter",    kind: "daily", clubId: 1, color: "#00e5ff" },
  { key: "d2", label: "Builder",    kind: "daily", clubId: 2, color: "#00e5ff" },
  { key: "d3", label: "Achiever",   kind: "daily", clubId: 3, color: "#00e5ff" },
  { key: "d4", label: "Leader",     kind: "daily", clubId: 4, color: "#a78bfa" },
  { key: "d5", label: "Mentor",     kind: "daily", clubId: 5, color: "#a78bfa" },
  { key: "d6", label: "Director",   kind: "daily", clubId: 6, color: "#ec4899" },
  { key: "d7", label: "Executive",  kind: "daily", clubId: 7, color: "#ec4899" },
];

function AdminRanks() {
  const notify = useNotification();
  const { getContract } = useWeb3();
  const [activeRank, setActiveRank] = useState("d1");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportFrom, setExportFrom] = useState("1");
  const [exportTo, setExportTo] = useState("");
  const cancelRef = useRef(false);

  const rank = RANK_TABS.find((r) => r.key === activeRank);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Fetch the list page + hydrate with user + current eligibility
  useEffect(() => {
    cancelRef.current = false;
    const load = async () => {
      const contract = getContract();
      if (!contract) return;
      setLoading(true);
      try {
        const offset = page * PAGE_SIZE;
        // Only daily royalties exist in the new contract
        const res = await contract.getDailyEligibleList(rank.clubId, offset, PAGE_SIZE);
        const list = res.list || res[0];
        const totalBn = res.total || res[1];
        if (!cancelRef.current) {
          setTotal(totalBn.toNumber ? totalBn.toNumber() : Number(totalBn.toString()));
        }

        const ids = list.map((x) => x.toString());

        // Hydrate user info + current eligibility (list is append-only, may include lost/blocked)
        const details = await Promise.all(
          ids.map(async (id) => {
            try {
              const u = await contract.users(id);
              let currentlyEligible = false;
              try {
                const elig = await contract.getUserEligibility(id);
                // ethers v5 unwraps single-return functions — `elig` IS the bool[7] array
                const arr = Array.isArray(elig) ? elig : (elig?.royaltyEligible || []);
                currentlyEligible = !!arr[rank.clubId - 1];
              } catch {}
              return {
                id,
                serialNumber: u.serialNumber.toString(),
                name: bytes32ToString(u.name),
                wallet: u.wallet,
                isBlocked: u.isBlocked,
                currentlyEligible,
              };
            } catch { return null; }
          })
        );
        if (!cancelRef.current) setRows(details.filter(Boolean));
      } catch {
        // ignore
      } finally {
        if (!cancelRef.current) setLoading(false);
      }
    };
    load();
    return () => { cancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRank, page]);

  // Reset page whenever rank changes
  useEffect(() => { setPage(0); }, [activeRank]);

  // When total changes, default the export "to" field to the full count
  useEffect(() => {
    if (total > 0) setExportTo(String(total));
  }, [total, activeRank]);

  const copyAddr = async (addr) => {
    try {
      await navigator.clipboard.writeText(addr);
      notify.success("Address copied");
    } catch { notify.error("Copy failed"); }
  };

  // CSV export — user-defined range (positional index in the append-only list)
  const exportCsv = async () => {
    const contract = getContract();
    if (!contract) return notify.error("Wallet not connected");
    if (total === 0) return notify.error("Nothing to export");

    const from = parseInt(exportFrom, 10);
    const to = parseInt(exportTo, 10);
    if (!from || from < 1) return notify.error("Invalid start position");
    if (!to || to < from) return notify.error("Invalid end position");
    if (from > total) return notify.error(`Start beyond total (${total})`);
    const clampedTo = Math.min(to, total);
    const count = clampedTo - from + 1;
    if (!window.confirm(`Export positions ${from} to ${clampedTo} (${count} ${rank.label} achievers)?`)) return;

    setExporting(true);
    setExportProgress(0);
    try {
      const chunk = 25;
      const allIds = [];
      // fromSerial-1 converts 1-indexed UI to 0-indexed contract offset
      const startOffset = from - 1;
      const endOffset = clampedTo;

      for (let start = startOffset; start < endOffset; start += chunk) {
        const limit = Math.min(chunk, endOffset - start);
        let res;
        try {
          res = await contract.getDailyEligibleList(rank.clubId, start, limit);
        } catch (err) {
          notify.error(`Failed at position ${start + 1}: ${err.message?.slice(0, 60) || "RPC error"}`);
          setExporting(false);
          setExportProgress(0);
          return;
        }
        const list = res.list || res[0];
        for (const x of list) allIds.push(x.toString());
      }

      // Hydrate details sequentially (RPC-friendly)
      const validRows = [];
      let processed = 0;
      for (const id of allIds) {
        try {
          const u = await contract.users(id);
          let currentlyEligible = false;
          try {
            const elig = await contract.getUserEligibility(id);
            const arr = Array.isArray(elig) ? elig : (elig?.royaltyEligible || []);
            currentlyEligible = !!arr[rank.clubId - 1];
          } catch {}
          validRows.push({
            serial: u.serialNumber.toString(),
            id,
            name: bytes32ToString(u.name).replace(/"/g, '""'),
            wallet: u.wallet,
            isBlocked: u.isBlocked ? "Y" : "N",
            currentlyEligible: currentlyEligible ? "Y" : "N",
          });
        } catch { /* skip */ }
        processed++;
        setExportProgress(Math.round((processed / allIds.length) * 100));
      }

      if (validRows.length === 0) {
        notify.error("No achievers found in that range");
        setExporting(false);
        setExportProgress(0);
        return;
      }

      const header = ["Serial No", "User ID", "Name", "Wallet", "Blocked", "Currently Eligible"];
      const lines = [header.join(",")];
      for (const r of validRows) {
        lines.push([r.serial, r.id, `"${r.name}"`, r.wallet, r.isBlocked, r.currentlyEligible].join(","));
      }
      const csvContent = "﻿" + lines.join("\r\n"); // BOM + Windows CRLF

      try {
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        a.href = url;
        a.download = `888global-${rank.label.toLowerCase()}-${from}-${clampedTo}-${ts}.csv`;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch {
        const dataUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);
        window.open(dataUri, "_blank");
      }
      notify.success(`Exported ${validRows.length} achievers`);
    } catch (e) {
      notify.error("Export failed: " + (e.message?.slice(0, 80) || "unknown error"));
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const setRange = (from, to) => {
    setExportFrom(String(from));
    setExportTo(String(Math.min(to, total)));
  };

  return (
    <div>
      <div className="section-header" style={{ marginTop: 0 }}>
        <div className="section-title">Rank Achievers</div>
      </div>

      {/* Rank selector chips */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 6,
        marginBottom: 14,
      }}>
        {RANK_TABS.map((r) => (
          <button
            key={r.key}
            onClick={() => setActiveRank(r.key)}
            style={{
              padding: "8px 6px",
              borderRadius: 8,
              border: activeRank === r.key ? `1px solid ${r.color}` : "1px solid var(--border-strong)",
              background: activeRank === r.key ? `${r.color}22` : "#0a0e24",
              color: activeRank === r.key ? r.color : "var(--text-secondary)",
              fontFamily: "var(--font-display)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.02em",
              cursor: "pointer",
              boxShadow: activeRank === r.key ? `0 0 10px ${r.color}44` : "none",
              transition: "all 150ms ease",
            }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Header stats */}
      <Card className="mb-md">
        <div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
            Ever Reached {rank?.label}
          </div>
          <div style={{
            fontFamily: "'Sora', 'Inter', sans-serif",
            fontSize: 28,
            fontWeight: 700,
            color: rank?.color,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            marginTop: 4,
          }}>
            {total.toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
            Includes users who may have since lost eligibility
          </div>
        </div>
      </Card>

      {/* CSV Export with range */}
      {total > 0 && (
        <Card className="mb-lg">
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
              Export {rank?.label} Achievers
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
              Positional range in the achiever list · CSV format
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", marginBottom: 10 }}>
            <div>
              <label className="input-label">From</label>
              <input type="number" className="input" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} min="1" max={total} disabled={exporting} />
            </div>
            <div style={{ paddingTop: 20, color: "var(--text-muted)", fontSize: 14 }}>→</div>
            <div>
              <label className="input-label">To</label>
              <input type="number" className="input" value={exportTo} onChange={(e) => setExportTo(e.target.value)} min="1" max={total} disabled={exporting} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            <RankPresetChip label="1-50" onClick={() => setRange(1, 50)} disabled={exporting} />
            <RankPresetChip label="1-100" onClick={() => setRange(1, 100)} disabled={exporting} />
            <RankPresetChip label="1-500" onClick={() => setRange(1, 500)} disabled={exporting} />
            <RankPresetChip label={`All (${total})`} onClick={() => setRange(1, total)} disabled={exporting} />
          </div>
          <Button variant="primary" size="sm" full onClick={exportCsv} loading={exporting}>
            {exporting ? `Exporting... ${exportProgress}%` : "⬇ Download CSV"}
          </Button>
        </Card>
      )}

      {/* List */}
      {loading ? (
        <div className="empty-state">
          <span className="spinner spinner-lg" />
          <div className="empty-state-text mt-md">Loading achievers...</div>
        </div>
      ) : rows.length === 0 ? (
        <Card><div className="empty-state-text">No achievers yet for {rank?.label}</div></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((r) => (
            <div key={r.id} style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: 10,
              background: "#0a0e24",
              border: `1px solid ${r.currentlyEligible ? rank.color + "55" : "var(--border-strong)"}`,
              borderRadius: 10,
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: "50%",
                background: r.currentlyEligible
                  ? `radial-gradient(circle at 30% 30%, ${rank.color}, ${rank.color}88)`
                  : "linear-gradient(135deg, #333, #222)",
                color: r.currentlyEligible ? "#000" : "var(--text-muted)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, flexShrink: 0,
                border: r.currentlyEligible ? `2px solid ${rank.color}` : "1px solid var(--border-strong)",
                boxShadow: r.currentlyEligible ? `0 0 10px ${rank.color}66` : "none",
              }}>
                ★
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                    {r.name || "—"}
                  </span>
                  {r.isBlocked && (
                    <span className="badge badge-red" style={{ fontSize: 9 }}>BLOCKED</span>
                  )}
                  {!r.currentlyEligible && !r.isBlocked && (
                    <span className="badge" style={{
                      fontSize: 9,
                      background: "rgba(139,155,209,0.08)",
                      color: "var(--text-muted)",
                      border: "1px solid var(--border-strong)",
                    }}>
                      LOST
                    </span>
                  )}
                  {r.currentlyEligible && (
                    <span className="badge" style={{
                      fontSize: 9,
                      background: rank.color,
                      color: "#000",
                      border: "none",
                      fontWeight: 700,
                    }}>
                      ACTIVE
                    </span>
                  )}
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--cyan)", marginTop: 2 }}>
                  #{r.id} · SN {r.serialNumber}
                </div>
              </div>
              <button
                onClick={() => copyAddr(r.wallet)}
                title={r.wallet}
                style={{
                  background: "transparent",
                  border: "1px solid var(--border-strong)",
                  color: "var(--text-muted)",
                  padding: "4px 8px",
                  borderRadius: 5,
                  fontSize: 9,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "var(--font-mono)",
                  flexShrink: 0,
                }}
              >
                {shortAddress(r.wallet, 3)}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination" style={{ marginTop: 14 }}>
          <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading}>‹ Prev</Button>
          <span className="pagination-info">Page {page + 1} / {totalPages}</span>
          <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1 || loading}>Next ›</Button>
        </div>
      )}
    </div>
  );
}

function RankPresetChip({ label, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "5px 10px",
        borderRadius: 6,
        border: "1px solid var(--cyan-border)",
        background: "rgba(0, 229, 255, 0.06)",
        color: "var(--cyan)",
        fontSize: 11,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        fontFamily: "var(--font-body)",
      }}
    >
      {label}
    </button>
  );
}

export default AdminRanks;
